import { Link } from "react-router-dom";
import { ArrowUpRight, ClipboardList, Stethoscope, UtensilsCrossed } from "lucide-react";
import { AppStoreButtons } from "@/components/AppStoreButtons";
import ComplianceStrip from "@/components/ComplianceStrip";
import useSeo from "@/hooks/useSeo";

const ROLES = [
  {
    id: "role-canteen",
    icon: UtensilsCrossed,
    tag: "01 / Canteen",
    title: "The hospital canteen — the root profile.",
    body:
      "Uploads daily menus for breakfast, lunch, evening and dinner. Receives validated, ward-wise diet orders the moment they are signed. All Nurse and Dietician accounts are created under this canteen.",
  },
  {
    id: "role-nurse",
    icon: Stethoscope,
    tag: "02 / Nurse",
    title: "The nurse enters patient vitals.",
    body:
      "Records patient UHID, vitals, comorbidities and diet preferences. Dietico computes carbohydrate, protein and fat requirements against a 1,200-item Indian food database aligned to NIN and FSSAI.",
  },
  {
    id: "role-dietician",
    icon: ClipboardList,
    tag: "03 / Dietician",
    title: "The dietician reviews and e-signs.",
    body:
      "Reviews every ward and ICU diet sheet, edits as needed, and applies an electronic signature under the IT Act 2000. If no dietician is on staff, ward diet sheets route directly to the canteen.",
  },
];

export default function Home() {
  useSeo({
    title: null,
    description:
      "Dietico is a hospital diet assessor mobile app that connects hospital canteens, nurses and dieticians on one verified, timestamped record. DPDP Act 2023 compliant. Available on iOS and Android.",
    path: "/",
  });
  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="mx-auto max-w-7xl px-6 pt-16 pb-24 md:pt-24 md:pb-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-end">
            <div className="lg:col-span-8 animate-fadeUp">
              <p className="font-mono text-[11px] uppercase tracking-widest text-secondary mb-6">
                A product of Mesron Healthcare LLP · Launched June 2026
              </p>
              <h1 className="text-5xl md:text-7xl lg:text-[88px] font-light tracking-tightest leading-[0.95] text-foreground">
                <span className="block">Ending paper-first</span>
                <span className="block">hospital diet chaos.</span>
              </h1>
              <p className="mt-8 max-w-2xl text-lg md:text-xl text-secondary leading-relaxed">
                Dietico is a clinical workflow platform that connects hospital
                canteens, nurses and dieticians on one verified, timestamped
                record — from vitals to ward diet sheet to tray.
              </p>
              <div className="mt-10">
                <AppStoreButtons />
              </div>
            </div>

            <div className="lg:col-span-4 relative animate-fadeUp" style={{ animationDelay: "120ms" }}>
              <div className="relative aspect-[4/5] rounded-3xl overflow-hidden border border-border bg-secondary noise-overlay">
                <img
                  src="https://images.unsplash.com/photo-1666886573452-9dc8ce8f5cc5?auto=format&fit=crop&w=1200&q=80"
                  alt="Clinician using a tablet at bedside"
                  className="absolute inset-0 h-full w-full object-cover"
                  data-testid="hero-image"
                />
                <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-black/70 to-transparent">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-white/70">
                    Available on
                  </p>
                  <p className="text-white text-sm mt-1">iOS · Android</p>
                </div>
              </div>
            </div>
          </div>

          {/* stat row */}
          <div className="mt-24 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-10 border-t border-border pt-12">
            {[
              ["1,200+", "Indian foods, NIN-aligned"],
              ["3", "Role-based profiles"],
              ["1 yr", "Diet order audit trail"],
              ["100%", "Data residency — India"],
            ].map(([n, l]) => (
              <div key={l}>
                <p className="text-3xl md:text-4xl font-light tracking-tight text-foreground">
                  {n}
                </p>
                <p className="mt-2 text-xs font-mono uppercase tracking-widest text-secondary">
                  {l}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ComplianceStrip />

      {/* ROLES / HOW IT WORKS */}
      <section
        data-testid="roles-section"
        className="mx-auto max-w-7xl px-6 py-24 md:py-32"
      >
        <div className="max-w-2xl mb-16">
          <p className="font-mono text-[11px] uppercase tracking-widest text-secondary mb-4">
            How it works
          </p>
          <h2 className="text-3xl md:text-5xl font-light tracking-tightest text-foreground">
            Three profiles. One verified record.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {ROLES.map((r, i) => {
            const Icon = r.icon;
            return (
              <article
                key={r.id}
                data-testid={r.id}
                className={`group relative rounded-2xl border border-border bg-secondary p-8 md:p-10 flex flex-col ${
                  i === 1 ? "md:translate-y-8" : ""
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-secondary">
                    {r.tag}
                  </span>
                  <Icon className="h-5 w-5 text-foreground" strokeWidth={1.5} />
                </div>
                <h3 className="mt-16 text-xl md:text-2xl tracking-tight text-foreground font-medium">
                  {r.title}
                </h3>
                <p className="mt-4 text-sm md:text-base text-secondary leading-relaxed">
                  {r.body}
                </p>
              </article>
            );
          })}
        </div>

        <p className="mt-12 max-w-3xl text-sm text-secondary leading-relaxed">
          Deleting the Hospital Canteen profile removes all Nurse and Dietician
          profiles for that hospital — a deliberate design decision consistent
          with the DPDP Act 2023 right-to-erasure and hospital-level data
          ownership.
        </p>
      </section>

      {/* MISSION STRIP */}
      <section className="border-t border-border">
        <div className="mx-auto max-w-7xl px-6 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <p className="font-mono text-[11px] uppercase tracking-widest text-secondary mb-6">
              Why we built it
            </p>
            <h2 className="text-3xl md:text-5xl font-light tracking-tightest text-foreground">
              The most consequential hospital failures are logistical — not clinical.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pl-8 lg:border-l lg:border-border">
            <p className="text-base md:text-lg text-secondary leading-relaxed">
              A patient with chronic kidney disease receiving a high-potassium
              meal because a paper chart was misread. A post-surgical patient
              given solid food because a verbal order was misheard. Dietico
              was built to make those errors impossible — with one source of
              truth for every diet order, zero verbal handovers, and full
              accountability on every edit.
            </p>
            <Link
              to="/about"
              data-testid="cta-about"
              className="mt-10 inline-flex items-center gap-2 text-sm font-medium text-foreground border-b border-foreground/40 pb-1 hover:border-foreground transition-colors"
            >
              Read our story <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="border-t border-border">
        <div className="mx-auto max-w-7xl px-6 py-24 md:py-32">
          <div className="rounded-3xl bg-secondary border border-border p-10 md:p-16 flex flex-col md:flex-row md:items-end md:justify-between gap-10">
            <div className="max-w-xl">
              <p className="font-mono text-[11px] uppercase tracking-widest text-secondary mb-4">
                Get the app
              </p>
              <h2 className="text-3xl md:text-4xl font-light tracking-tightest text-foreground">
                Dietico is available on iOS and Android.
              </h2>
              <p className="mt-4 text-secondary">
                Built for Indian hospitals. Data stays in India.
              </p>
            </div>
            <AppStoreButtons />
          </div>
        </div>
      </section>
    </div>
  );
}
